import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNodesState, useEdgesState, Node, NodeChange } from 'reactflow';
import styled, { useTheme } from 'styled-components';
import { FileText, Brain, User, MessageCircle, Database, Maximize2, Minimize2 } from 'lucide-react';
import { FlowCanvas } from './FlowCanvas';
import { ScriptEditorModal } from './ScriptEditorModal';
import { ConfigPanel } from './ConfigPanel';
import { useFlowNodes } from '../../hooks/useFlowNodes';
import { useScripts, useUpdateScript, useUpdateFatosProduto } from '../../hooks/useApi';
import type { BotFlowBuilderProps, ScriptNodeData, ScriptType } from './types';
import { showSuccessToast, showErrorToast } from '../../utils/toastHelpers';
import SkeletonLoader from '../SkeletonLoader';
import { useSkeletonDelay } from '../../hooks/useSkeletonDelay';

const BuilderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 300px);
  min-height: 600px;
  gap: 0;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};

  /* Quando em fullscreen, ocupar tela toda */
  &:fullscreen {
    height: 100vh;
    min-height: 100vh;
    border-radius: 0;
    border: none;
  }
`;

const FlowSection = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;

  /* Em fullscreen, ocupar todo espaço disponível */
  :fullscreen & {
    height: 100%;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.error};
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const ShortcutsLegend = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing[4]};
  right: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[3]};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ShortcutItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ShortcutKey = styled.kbd`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  font-family: monospace;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

// ===== GUIA INFORMATIVA =====
const InfoGuide = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  flex-shrink: 0;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 2px;
  }
`;

const GuideItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  white-space: nowrap;

  svg {
    color: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

const GuideLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GuideDescription = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const GuideDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const FullscreenButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  color: ${({ theme }) => theme.colors.textTertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  cursor: pointer;
  transition: all 0.15s ease;
  margin-top: ${({ theme }) => theme.spacing[2]};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

/**
 * BotFlowBuilder - Construtor Visual de Bot estilo n8n
 * Unifica edição de scripts + configurações estáticas em uma interface flow-based
 */
export const BotFlowBuilder: React.FC<BotFlowBuilderProps> = ({ userId }) => {
  const theme = useTheme();

  // Carregar scripts do backend
  const { data: scriptsData, isLoading, error } = useScripts(userId || undefined);

  // Mutations para salvamento
  const updateScriptMutation = useUpdateScript();
  const updateFatosProdutoMutation = useUpdateFatosProduto();

  // Converter scripts para nós do flow
  const { nodes: initialNodes, edges: initialEdges } = useFlowNodes(scriptsData?.data);

  // Estado do Flow Canvas
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Estado do modal de edição
  const [selectedNode, setSelectedNode] = useState<Node<ScriptNodeData> | null>(null);
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);

  // Estado do ConfigPanel (minimizado por padrão para melhor UX)
  const [isConfigMinimized, setIsConfigMinimized] = useState(true);

  // Estado de fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ===== SISTEMA DE UNDO/REDO ESTILO CANVA =====
  const undoStack = useRef<Node<ScriptNodeData>[][]>([]);
  const redoStack = useRef<Node<ScriptNodeData>[][]>([]);
  const isDragging = useRef(false);
  const dragStartState = useRef<Node<ScriptNodeData>[] | null>(null);

  // Deep clone para preservar estado completo
  const cloneNodes = useCallback((nodes: Node<ScriptNodeData>[]) => {
    return nodes.map(node => ({
      ...node,
      position: { ...node.position },
      data: { ...node.data },
    }));
  }, []);

  // Salvar estado no histórico
  const saveToHistory = useCallback((currentNodes: Node<ScriptNodeData>[]) => {
    undoStack.current.push(cloneNodes(currentNodes));
    // Limpar redo quando nova ação é feita
    redoStack.current = [];
    // Limitar histórico a 100 estados
    if (undoStack.current.length > 100) {
      undoStack.current.shift();
    }
  }, [cloneNodes]);

  // Atualizar nós quando scripts mudarem
  React.useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    // Limpar histórico quando scripts mudarem (novo estado base)
    undoStack.current = [];
    redoStack.current = [];
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Handler customizado de mudanças em nós (com histórico preciso)
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    // Detectar início do drag
    const dragStart = changes.some(change =>
      change.type === 'position' && (change as any).dragging === true
    );

    // Detectar fim do drag
    const dragEnd = changes.some(change =>
      change.type === 'position' && (change as any).dragging === false
    );

    // Salvar estado ANTES de começar a arrastar
    if (dragStart && !isDragging.current) {
      isDragging.current = true;
      setNodes((currentNodes) => {
        dragStartState.current = cloneNodes(currentNodes);
        return currentNodes;
      });
    }

    // Quando terminar de arrastar, salvar estado inicial no histórico
    if (dragEnd && isDragging.current) {
      isDragging.current = false;
      if (dragStartState.current) {
        undoStack.current.push(dragStartState.current);
        redoStack.current = []; // Limpar redo
        if (undoStack.current.length > 100) {
          undoStack.current.shift();
        }
        dragStartState.current = null;
      }
    }

    onNodesChange(changes);
  }, [onNodesChange, setNodes, cloneNodes]);

  // Listener para Ctrl+Z (undo) e Ctrl+Shift+Z / Ctrl+Y (redo)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isCtrl = event.ctrlKey || event.metaKey;

      // Ctrl+Z = Undo
      if (isCtrl && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();

        if (undoStack.current.length > 0) {
          setNodes((currentNodes) => {
            // Salvar estado atual no redo antes de desfazer
            redoStack.current.push(cloneNodes(currentNodes));
            const previousState = undoStack.current.pop()!;
            return previousState;
          });
        }
      }

      // Ctrl+Shift+Z ou Ctrl+Y = Redo
      if (isCtrl && ((event.key === 'z' && event.shiftKey) || event.key === 'y')) {
        event.preventDefault();

        if (redoStack.current.length > 0) {
          setNodes((currentNodes) => {
            // Salvar estado atual no undo antes de refazer
            undoStack.current.push(cloneNodes(currentNodes));
            const nextState = redoStack.current.pop()!;
            return nextState;
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setNodes, cloneNodes]);

  // Handler: Ctrl + Click em nó → abre modal
  const handleNodeClick = useCallback((event: any, node: Node<ScriptNodeData>) => {
    // Verificar se Ctrl (ou Cmd no Mac) está pressionado
    if (event.ctrlKey || event.metaKey) {
      console.log('📝 Ctrl + Click no nó:', node.data?.scriptName);

      setSelectedNode(node);
      setIsEditorModalOpen(true);
    }
  }, []);

  // Handler: Salvar script editado
  const handleSaveScript = async (content: string) => {
    if (!selectedNode || !userId) {
      showErrorToast('Erro: Nó ou usuário não selecionado', theme);
      return;
    }

    const { scriptType, scriptName } = selectedNode.data;

    try {
      console.log('💾 Salvando script:', { scriptType, scriptName, userId });

      if (scriptType === 'fatos_produto') {
        await updateFatosProdutoMutation.mutateAsync({
          conteudo: content,
          userId,
        });
      } else {
        await updateScriptMutation.mutateAsync({
          tipo: scriptType,
          nome: scriptName,
          conteudo: content,
          userId,
        });
      }

      showSuccessToast(`Script "${scriptName}" salvo com sucesso!`, theme);
      setIsEditorModalOpen(false);
      setSelectedNode(null);
    } catch (error) {
      console.error('❌ Erro ao salvar script:', error);
      showErrorToast('Erro ao salvar script. Tente novamente.', theme);
    }
  };

  // Handler: Fechar modal
  const handleCloseModal = useCallback(() => {
    setIsEditorModalOpen(false);
    setSelectedNode(null);
  }, []);

  // Handler: Toggle ConfigPanel
  const handleToggleConfig = useCallback(() => {
    setIsConfigMinimized((prev) => !prev);
  }, []);

  // Handler: Toggle Fullscreen
  const handleToggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  // Listener para detectar mudança de fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Loading state com skeleton
  const showSkeleton = useSkeletonDelay(isLoading, 1000);

  if (showSkeleton) {
    return <SkeletonLoader variant="nucleoNeural" />;
  }

  // Error state
  if (error) {
    return (
      <BuilderContainer>
        <ErrorState>
          <h3>Erro ao carregar scripts</h3>
          <p>Não foi possível conectar ao backend. Verifique se o servidor está rodando.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              background: theme.colors.error,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '16px',
            }}
          >
            Tentar Novamente
          </button>
        </ErrorState>
      </BuilderContainer>
    );
  }

  return (
    <BuilderContainer ref={containerRef} style={isFullscreen ? { height: '100vh', minHeight: '100vh' } : undefined}>
      {/* Guia Informativa - explica cada tipo de script */}
      <InfoGuide>
        <GuideItem $color="#6b7280">
          <FileText size={14} />
          <GuideLabel>Conteúdo</GuideLabel>
          <GuideDescription>O que o bot diz em cada situação</GuideDescription>
        </GuideItem>

        <GuideDivider />

        <GuideItem $color="#ef4444">
          <Brain size={14} />
          <GuideLabel>Mentalidade</GuideLabel>
          <GuideDescription>Como age conforme cada conteúdo</GuideDescription>
        </GuideItem>

        <GuideDivider />

        <GuideItem $color="#8b5cf6">
          <User size={14} />
          <GuideLabel>Persona</GuideLabel>
          <GuideDescription>Personalidade e tom de voz do bot</GuideDescription>
        </GuideItem>

        <GuideDivider />

        <GuideItem $color="#10b981">
          <MessageCircle size={14} />
          <GuideLabel>Escrita</GuideLabel>
          <GuideDescription>Gírias, abreviações e emojis permitidos</GuideDescription>
        </GuideItem>

        <GuideDivider />

        <GuideItem $color="#f59e0b">
          <Database size={14} />
          <GuideLabel>Produto</GuideLabel>
          <GuideDescription>Informações sobre o que está vendendo</GuideDescription>
        </GuideItem>
      </InfoGuide>

      {/* Flow Canvas (área principal) */}
      <FlowSection>
        <FlowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
        />

        {/* Legenda de atalhos */}
        <ShortcutsLegend>
          <ShortcutItem>
            <ShortcutKey>Ctrl</ShortcutKey>
            <span>+</span>
            <ShortcutKey>Click</ShortcutKey>
            <span>Editar</span>
          </ShortcutItem>
          <ShortcutItem>
            <ShortcutKey>Ctrl</ShortcutKey>
            <span>+</span>
            <ShortcutKey>Z</ShortcutKey>
            <span>Desfazer</span>
          </ShortcutItem>
          <ShortcutItem>
            <ShortcutKey>Ctrl</ShortcutKey>
            <span>+</span>
            <ShortcutKey>Y</ShortcutKey>
            <span>Refazer</span>
          </ShortcutItem>
          <FullscreenButton onClick={handleToggleFullscreen} title={isFullscreen ? 'Sair da tela cheia (Esc)' : 'Tela cheia'}>
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {isFullscreen ? 'Sair' : 'Expandir'}
          </FullscreenButton>
        </ShortcutsLegend>
      </FlowSection>

      {/* Config Panel (painel inferior) */}
      <ConfigPanel
        userId={userId}
        isMinimized={isConfigMinimized}
        onToggleMinimize={handleToggleConfig}
      />

      {/* Script Editor Modal */}
      {isEditorModalOpen && selectedNode && (
        <ScriptEditorModal
          scriptType={selectedNode.data.scriptType}
          scriptName={selectedNode.data.scriptName}
          content={selectedNode.data.content}
          onSave={handleSaveScript}
          onClose={handleCloseModal}
          loading={updateScriptMutation.isPending || updateFatosProdutoMutation.isPending}
        />
      )}
    </BuilderContainer>
  );
};
